import React from 'react';
import { useParams } from 'next/navigation';
import round from 'lodash/round';
import cloneDeep from 'lodash/cloneDeep';
import Grid from '@mui/material/Grid';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import SeoSwiper from '@/components/swiper/SeoSwiper';
import MobileSwiper from '@/components/mobile/MobileSwiper';
import SimilarJobTitleCard from './Card';
import { SimilarJobTitleType } from '@/context/job-title';
import { JobTitleContext } from '@/context/create-context';
import { useClintMediaQuery } from '@/utils/hooks';

const SimilarJobTitle = (props: { data: SimilarJobTitleType[] }) => {
  const { data } = props;
  const isMobile = useClintMediaQuery();
  const {
    jobTitle: { city_name: cityName = '' },
    displayName,
  } = React.useContext(JobTitleContext);
  const cityQuery = (useParams()?.city as string) || undefined;

  const seoValue = React.useMemo(() => {
    const salaryJobSortData = cloneDeep(data).sort(
      (a, b) => b.salary - a.salary,
    );
    const mostSalaryJob = salaryJobSortData[0];
    const minSalaryJob = salaryJobSortData[salaryJobSortData.length - 1];

    return {
      relatedJobs: data.map((item) => `${cityName}${item.name}`).join('、'),
      mostSalaryJob: `${cityName}${mostSalaryJob.name}`,
      mostSalaryValue: round(mostSalaryJob.salary, -2),
      minSalaryJob: `${cityName}${minSalaryJob.name}`,
      minSalaryJobValue: round(minSalaryJob.salary, -2),
    };
  }, []);

  const {
    relatedJobs,
    mostSalaryJob,
    mostSalaryValue,
    minSalaryJob,
    minSalaryJobValue,
  } = seoValue;

  return (
    <div className="text-black">
      <p className="text-xl font-semibold tablet:text-2xl tablet:font-medium">
        类似职位
      </p>
      <Grid container={!isMobile} spacing={isMobile ? undefined : 2}>
        <Grid item={!isMobile} className="w-full">
          <DataInterpretationSEO
            lineClamp="line-clamp-none"
            className="text-opacity-100 font-normal"
          >
            {`想知道和${cityName}${displayName}类似的职位有哪些吗？${relatedJobs}，这些职位都有着一定的共同技能。其中，${
              minSalaryJob !== mostSalaryJob &&
              `${minSalaryJob}的平均月薪最低，为¥${minSalaryJobValue}/月，`
            }${mostSalaryJob}的平均月薪最高，为¥${mostSalaryValue}/月。本数据依据于各平台公开数据分析得出，仅供参考。`}
          </DataInterpretationSEO>
          <div className="mt-8 tablet:mt-4 w-full">
            <MobileSwiper
              chunkSize={6}
              slideClassName="grid grid-cols-2 grid-rows-3 gap-x-3 gap-y-4"
              chunkData={data}
              swiperEle={({ id, name, salary, description }) => (
                <SimilarJobTitleCard
                  key={id + cityName}
                  id={id}
                  city={{ name: cityName, pinyin: cityQuery }}
                  name={name}
                  salary={salary ?? 'NaN'}
                  isJobTitlePage={true}
                  description={description}
                  dataEleGroup="job-title_similar-job"
                />
              )}
            />
            <SeoSwiper
              chunkData={data}
              chunkSize={8}
              slideClassName="grid-cols-4 grid-rows-2"
              swiperEle={({ id, name, salary, description }) => (
                <SimilarJobTitleCard
                  key={id + cityName}
                  id={id}
                  city={{ name: cityName, pinyin: cityQuery }}
                  name={name}
                  salary={salary ?? 'NaN'}
                  isJobTitlePage={true}
                  description={description}
                  dataEleGroup="job-title_similar-job"
                />
              )}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default SimilarJobTitle;
